import { BigNumberOccurrence } from "../../../models/occurrence";
import { useLocation } from "react-router-dom";
import { formatMilisecondsInDaysHours } from "../../../utils/dateUtils";
import { useEffect, useLayoutEffect, useRef } from "react";

const CARD_CHROME = 34;
const CARDS_GAP = 16;
const STACK_BELOW = 16;

const useFitFont = (
  rootRef: React.RefObject<HTMLDivElement>,
  contentRefs: React.RefObject<HTMLDivElement>[],
  deps: React.DependencyList,
  max = 20,
  min = 10
) => {
  const fitRef = useRef<() => void>(() => {});

  fitRef.current = () => {
    const root = rootRef.current;
    const contents = contentRefs.map((ref) => ref.current).filter((el): el is HTMLDivElement => !!el);
    if (!root || !contents.length) return;
    const apply = (size: number) => contents.forEach((el) => (el.style.fontSize = `${size}px`));
    const fit = (available: number, needed: () => number) => {
      let size = max;
      apply(size);
      while (size > min && needed() > available) {
        size -= 0.5;
        apply(size);
      }
      return size;
    };
    const sideBySide = fit(
      root.clientWidth - CARD_CHROME * contents.length - CARDS_GAP * (contents.length - 1),
      () => contents.reduce((sum, el) => sum + el.scrollWidth, 0)
    );
    if (sideBySide >= STACK_BELOW) return;
    fit(root.clientWidth - CARD_CHROME, () => Math.max(...contents.map((el) => el.scrollWidth)));
  };

  useLayoutEffect(() => fitRef.current(), deps);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => fitRef.current());
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
};

export const BigNumbers = (props: {
  bigNumberAnalytics: BigNumberOccurrence;
  finishedFilter: string[];
  loading?: boolean;
  isFilteredByStep?: boolean | null;
}) => {
  const location = useLocation();
  const isHistorico = location.pathname.includes("history");

  const colorPrimary = isHistorico ? "text-color-dark" : "text-color-primary";
  const colorWarning = isHistorico ? "text-color-dark" : "text-color-warning";

  const quantityWidgets = [
    (props.finishedFilter.includes('true') || props.finishedFilter.length === 0) && {
      label: "Chamados concluídos",
      value: Math.max(props.bigNumberAnalytics.totalOfFinalizedOccurrence, 0),
      color: colorPrimary,
    },
    (!props.isFilteredByStep && props.finishedFilter.includes('false') || props.finishedFilter.length === 0) && {
      label: "Chamados não iniciados",
      value: Math.max(props.bigNumberAnalytics.totalOfNotStartedOccurrence, 0),
      color: colorPrimary,
    },
    (props.finishedFilter.includes('false') || props.finishedFilter.length === 0) && {
      label: "Chamados em andamento",
      value: Math.max(props.bigNumberAnalytics.totalOfInProgressOccurrence, 0),
      color: colorPrimary,
    },
    {
      label: "Veículos",
      value: Math.max(props.bigNumberAnalytics.totalChassis ?? 0, 0),
      color: colorPrimary,
    },
  ].filter((el): el is { label: string; value: number; color: string } => el !== false);

  const timeWidgets = [
    // { label: "Tempo total em serviço", value: formatMilisecondsInDaysHours(props.bigNumberAnalytics.totalInService * 1000) || '0h', color: colorWarning },
    { label: "Tempo médio por veículo", value: formatMilisecondsInDaysHours(props.bigNumberAnalytics.averageInService * 1000) || '0h', color: colorWarning },
    // { label: "Tempo médio por chamado", value: formatMilisecondsInDaysHours(props.bigNumberAnalytics.averageInServicePerOccurrence * 1000) || '0h', color: colorWarning },
  ];

  const rootRef = useRef<HTMLDivElement>(null);
  const content1Ref = useRef<HTMLDivElement>(null);
  const content2Ref = useRef<HTMLDivElement>(null);

  useFitFont(rootRef, [content1Ref, content2Ref], [quantityWidgets.length, props.loading, props.bigNumberAnalytics], 24, 9);

  const renderItems = (items: { label: string; value: any; color: string }[]) =>
    items.map((item, index) => (
      <div key={index} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 0.6em' }}>
          <span className={item.color} style={{ fontSize: '2.4em', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {props.loading ? '-' : item.value}
          </span>
          <span style={{ fontSize: '0.9em', color: '#555', marginTop: 2, whiteSpace: 'nowrap' }}>
            {item.label}
          </span>
        </div>
        {index !== items.length - 1 && (
          <div style={{ width: 1, height: '2.5em', background: '#ccc', flexShrink: 0 }} />
        )}
      </div>
    ));

  const cardWrapperStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: '8px 16px',
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div ref={rootRef} style={{ display: 'flex', flexWrap: 'wrap', gap: 16, width: '100%', alignItems: 'stretch', justifyContent: 'flex-end' }}>

      <div style={cardWrapperStyle}>
        <div ref={content1Ref} style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center' }}>
          {renderItems(quantityWidgets)}
        </div>
      </div>

      <div style={cardWrapperStyle}>
        <div ref={content2Ref} style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center' }}>
          {renderItems(timeWidgets)}
        </div>
      </div>

    </div>
  );
};

